/**
 * Team — **the current organization's members**, drawn from the design's
 * `Led AI - Team Web.dc.html` artboard (project `806e74fa`, design system
 * `f7c932`).
 *
 * One list, stats first. Every member of the Clerk organization this
 * workspace is, the role an admin actually assigned, and what their walks
 * produced — broken out by kind, because "records" as one number cannot tell
 * a superintendent raising punch items from one filing daily logs.
 * `server/queries/getPeople.ts` explains at length why the previous three
 * groups — field, office, partners — were removed.
 *
 * ## A member with no walks is still a member
 *
 * Zero-walk rows are the ones this page most needs to draw. They are the
 * difference between a SEAT roster and a walker list, and dropping them would
 * make somebody who took a week off look like somebody who left the company.
 * The row says which of the two quiet states it is: never signed in at all,
 * or signed in with no walks in this workspace. Last walk is all-time within
 * the workspace, so a quiet period does not erase someone's walk history.
 *
 * ## Two people really are called the same thing
 *
 * The real organization has two members whose Clerk profile name is
 * identically "Leo Li". `nameCollides` marks exactly those rows and the email
 * is shown in place of the role, so the list never renders two people nobody
 * can tell apart. Everyone else gets the role, which is the more useful fact.
 *
 * ## The Led AI-internal Procore layer
 *
 * For a Led AI staff reader who is an ADMIN of the workspace the page also
 * carries what the pilot's people record in PROCORE: a "Records in Procore" card above the roster, two more
 * roster columns (the weekly trend and the change against baseline), and a
 * row for each chosen person who is not on Led AI yet, with the one thing to
 * do about that — Send invite. Customers, and members of the workspace who
 * are not its admins, never see any of it; see `TeamProcore.tsx`, which owns
 * everything Procore on this page.
 *
 * ## Personal workspace says there is no organization
 *
 * Exactly as the workspace switcher does. It is not an error and not an empty
 * team — and a Clerk failure gets a DIFFERENT sentence, because "there is
 * nobody" and "we could not ask" must never render the same way.
 *
 * ## Colours are the design system's, not `C`
 *
 * This page is drawn from the artboard, whose ink is a true near-black and
 * whose greys are its own, so it reads `DS` (tokens.ts) rather than the
 * console's warm `C`. Mixing the two on one screen would show.
 */

import { useRef, useState } from "react";
import { fetchPeople, type PeopleMemberRow, type PeopleResponse } from "../api";
import { MemberUsage } from "./MemberUsage";
import { useAuthPerson } from "../auth/identity";
import {
  DsButton,
  DsIcon,
  DsPill,
  PROCORE_SPARK_W,
  PendingPersonRow,
  ProcoreCard,
  ProcoreCells,
  indexPull,
  looksLikeLedStaff,
  useInvitations,
  TEAM_INVITES,
  useProcoreBaseline,
  DEFAULT_LENS,
  type ProcoreIndex,
  type ProcoreLens,
} from "./TeamProcore";
import { useResource } from "../useResource";
import { DS, FONT_MONO } from "../tokens";
import { AnchoredPanel } from "../components/AnchoredPanel";
import { TEAM_PAGE as PAGE } from "../layout";
import {
  ASK_COMPOSER_CLEARANCE,
  ConsoleViewProps,
  EmptyPanel,
  FailurePanel,
  LoadingPanel,
  StaleBanner,
} from "../components/chrome";

// ---------------------------------------------------------------------------
// The artboard's own values
// ---------------------------------------------------------------------------

/**
 * The scrolling pane, NOT `ViewBody`. The artboard sets its own column —
 * 1080 wide with 32px inside it — and `ViewBody`'s 72px gutter nested inside
 * that would narrow the table below what the design draws. The clearance and
 * the scrollbar class are `ViewBody`'s, because those are pane behaviour.
 */
const PANE: React.CSSProperties = {
  flex: 1,
  minHeight: 0,
  overflow: "auto",
  padding: `0 0 ${ASK_COMPOSER_CLEARANCE}px`, paddingBottom: `var(--ask-composer-clearance, ${ASK_COMPOSER_CLEARANCE}px)`,
};

/** Every card on the page: white, the 7% hairline, 16px radius, no shadow. */
const CARD: React.CSSProperties = {
  background: DS.surface,
  border: `1px solid ${DS.line}`,
  borderRadius: 16,
};

/** The column heads and the mono meta — 9px, 700, letterspaced, the label grey. */
const HEAD: React.CSSProperties = {
  fontFamily: FONT_MONO,
  fontSize: 9,
  fontWeight: 700,
  letterSpacing: "1.1px",
  textTransform: "uppercase",
  color: DS.meta,
  whiteSpace: "nowrap",
};

/**
 * The table's columns, declared once because the header and every row have
 * to share them exactly. Person, walks, five record kinds, last walk — and,
 * with the Procore layer on, the weekly trend and the change.
 */
const RECORD_COLUMNS = ["Logs", "Obs", "Tasks", "Punch", "Photos"] as const;
const BASE_GRID = `minmax(170px,1.4fr) 52px repeat(${RECORD_COLUMNS.length},44px) minmax(96px,.8fr)`;
const PROCORE_GRID = `${BASE_GRID} ${PROCORE_SPARK_W}px 56px`;
/** The artboard's table never squeezes below this; the card scrolls instead. */
const TABLE_MIN_WIDTH = 780;

/** Rolling windows; seven days is not the current calendar week. */
const RANGES: Array<{ id: string; label: string; days: number }> = [
  { id: "week", label: "Last 7 days", days: 7 },
  { id: "30d", label: "Last 30 days", days: 30 },
  { id: "90d", label: "Last 90 days", days: 90 },
];

/** The window with no `?withinDays=`: 90 days, a pilot's worth. MUST equal the
 *  server's `DEFAULT_PEOPLE_WINDOW_DAYS` (`queries/getPeople.ts`) — a literal
 *  rather than an import because that module carries Mongoose models the
 *  browser bundle must never see; `adminViews.test.tsx` pins the two together. */
const DEFAULT_DAYS = 90;

/** How many rows the table shows before "Show all N people". */
const TOP_ROWS = 6;

// ---------------------------------------------------------------------------
// Pieces
// ---------------------------------------------------------------------------

/** The window for counts — last walk remains all-time. A 40px white pill
 *  with the design system's calendar glyph and a chevron. */
function RangePill({ activeDays, onPick }: { activeDays: number; onPick: (days: number) => void }) {
  const [open, setOpen] = useState(false);
  const trigger = useRef<HTMLButtonElement>(null);
  const label = RANGES.find((range) => range.days === activeDays)?.label ?? `Last ${activeDays} days`;

  return (
    <>
      <button
        ref={trigger}
        data-testid="people-range"
        className="kb"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((value) => !value)}
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 8,
          height: 40,
          padding: "0 14px",
          borderRadius: 999,
          background: DS.surface,
          border: "1px solid rgba(10,10,10,.1)",
          fontFamily: "inherit",
          fontSize: 14,
          fontWeight: 600,
          color: DS.ink,
          whiteSpace: "nowrap",
          cursor: "pointer",
        }}
      >
        <DsIcon name="calendar" size={14} color={DS.body2} />
        {label}
        <DsIcon name="chevron" size={11} color={DS.body2} style={{ transform: open ? "rotate(-90deg)" : "rotate(90deg)" }} />
      </button>
      {open && (
        <AnchoredPanel anchorRef={trigger} onClose={() => setOpen(false)} label="Window" role="menu" minWidth={168} testId="people-range-menu">
          {RANGES.map((range) => (
            <button
              key={range.id}
              role="menuitemradio"
              aria-checked={range.days === activeDays}
              data-testid="people-range-option"
              data-days={String(range.days)}
              className="navrow"
              onClick={() => {
                setOpen(false);
                onPick(range.days);
              }}
              style={{
                display: "flex",
                alignItems: "center",
                width: "100%",
                height: 36,
                padding: "0 10px",
                border: "none",
                borderRadius: 8,
                background: "transparent",
                fontFamily: "inherit",
                fontSize: 14,
                fontWeight: range.days === activeDays ? 700 : 500,
                color: DS.ink,
                cursor: "pointer",
                textAlign: "left",
              }}
            >
              {range.label}
            </button>
          ))}
        </AnchoredPanel>
      )}
    </>
  );
}

/** The design system's StatBand: value over mono label, columns split by
 *  left hairlines, no boxes. */
function StatBand({ stats }: { stats: Array<[string, string]> }) {
  return (
    <div data-testid="team-stats" style={{ ...CARD, padding: "18px 22px", display: "flex" }}>
      {stats.map(([value, label], i) => (
        <div key={label} style={{ flex: 1, minWidth: 0, paddingLeft: i === 0 ? 0 : 12, borderLeft: i === 0 ? "none" : `1px solid ${DS.line}` }}>
          <div style={{ fontSize: 18, fontWeight: 600, color: DS.ink, letterSpacing: "-0.02em", lineHeight: 1.1 }}>{value}</div>
          <div style={{ fontFamily: FONT_MONO, fontSize: 10, color: DS.ink2, letterSpacing: "0.11em", textTransform: "uppercase", marginTop: 3, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{label}</div>
        </div>
      ))}
    </div>
  );
}

/**
 * How long ago, in the design's own words: `6h ago`, `Yesterday`, `2d ago`.
 *
 * ELAPSED time, not calendar days. The column's question is "how long since
 * this person walked", and answering it in calendar days would need a
 * timezone — which this console does not hold for the reader, and which
 * `MemberUsage` refuses to guess for exactly this reason.
 */
function agoStamp(iso: string, now: number): string {
  const hours = Math.floor((now - new Date(iso).getTime()) / 3_600_000);
  if (hours < 1) return "Just now";
  if (hours < 24) return `${hours}h ago`;
  if (hours < 48) return "Yesterday";
  return `${Math.floor(hours / 24)}d ago`;
}

/** The last-walk stamp: when they last walked, or WHY there is no walk. Two
 *  silences, told apart, in the artboard's words. */
function activityStamp(person: PeopleMemberRow, now: number): string {
  if (person.lastWalkAt) return agoStamp(person.lastWalkAt, now);
  if (person.lastSignInAt === null) return "Hasn’t signed in";
  return "No walks yet";
}

/** One member row: avatar, who they are, and the numbers. The whole row is a
 *  LINK to that person's usage. */
function MemberRow({
  person,
  href,
  now,
  onOpen,
  procore,
}: {
  person: PeopleMemberRow;
  href: string;
  /** The page's ONE clock, read once per render above. */
  now: number;
  onOpen: () => void;
  /** The Procore layer, when a staff reader has one — two more cells. */
  procore?: { index: ProcoreIndex; lens: ProcoreLens };
}) {
  const walked = person.walks > 0;
  // Two members really can render the same name. When they do, the email is
  // the thing that tells them apart, so it takes the role's place.
  const role = person.nameCollides && person.email ? person.email : person.role === "admin" ? "Admin" : "Member";
  const counts = [person.records.log, person.records.observation, person.records.task, person.records.punch, person.records.photo];

  return (
    <a
      className="kb teamrow"
      data-testid="member-person"
      data-user-id={person.userId}
      data-role={person.role}
      href={href}
      onClick={(event) => {
        event.preventDefault();
        onOpen();
      }}
      style={{
        display: "grid",
        gridTemplateColumns: procore ? PROCORE_GRID : BASE_GRID,
        alignItems: "center",
        gap: "0 6px",
        minHeight: 66,
        padding: "10px 18px",
        borderBottom: "1px solid rgba(10,10,10,.06)",
        color: "inherit",
      }}
    >
      <span style={{ display: "flex", alignItems: "center", gap: 12, minWidth: 0 }}>
        <span
          style={{
            width: 36,
            height: 36,
            borderRadius: "50%",
            // A seat nobody has ever used reads as absent at a glance.
            background: person.lastSignInAt === null ? DS.dashed : DS.ink,
            color: DS.surface,
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            fontWeight: 700,
            fontSize: 14,
            letterSpacing: "-0.02em",
            flexShrink: 0,
          }}
        >
          {person.init}
        </span>
        <span style={{ display: "flex", flexDirection: "column", minWidth: 0 }}>
          <span style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0 }}>
            <span style={{ fontSize: 15, fontWeight: 700, letterSpacing: "-0.2px", color: DS.ink, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{person.name}</span>
            {person.isYou && <DsPill tone="gold">You</DsPill>}
          </span>
          <span data-testid="member-role" style={{ fontSize: 12.5, fontWeight: 500, color: DS.meta, marginTop: 2, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
            {role}
          </span>
        </span>
      </span>

      <span style={{ fontSize: 15, fontWeight: 700, color: walked ? DS.ink : DS.faint, textAlign: "right", fontVariantNumeric: "tabular-nums" }}>{person.walks}</span>

      {counts.map((count, index) => (
        <span key={RECORD_COLUMNS[index]} style={{ fontSize: 14, fontWeight: 600, color: count ? DS.ink : DS.faint, textAlign: "right", fontVariantNumeric: "tabular-nums" }}>
          {/* A zero is DRAWN, faint. Blanking it would make a member who raised
              no punch items look like one the column does not apply to. */}
          {count}
        </span>
      ))}

      <span style={{ minWidth: 0, paddingLeft: 10 }}>
        <span
          data-testid="member-last-walk"
          title={person.lastWalkAt ? `Latest walk in this workspace: ${new Date(person.lastWalkAt).toLocaleString()}` : undefined}
          style={{ fontSize: 13.5, fontWeight: 500, color: DS.meta, whiteSpace: "nowrap" }}
        >
          {activityStamp(person, now)}
        </span>
      </span>

      {procore && <ProcoreCells index={procore.index} userId={person.userId} lens={procore.lens} />}
    </a>
  );
}

// ---------------------------------------------------------------------------
// The page
// ---------------------------------------------------------------------------

/**
 * The Team screen: the roster, or ONE member's usage when `?person=` names
 * somebody. A dispatcher with no hooks of its own, so each page owns its own
 * fetch without a conditional hook.
 */
export function People(props: ConsoleViewProps) {
  const personId = new URLSearchParams(props.search).get("person");
  if (!personId) return <TeamRoster {...props} />;
  const backHref = props.hrefWithParam("person", null);
  return <MemberUsage {...props} personId={personId} backHref={backHref} onBack={() => props.onNavigate(backHref)} />;
}

function TeamRoster({ search, refreshKey, personalHref, onGoPersonal, hrefWithParam, onNavigate }: ConsoleViewProps) {
  const [showAll, setShowAll] = useState(false);
  const params = new URLSearchParams(search);
  const rawDays = Number(params.get("withinDays"));
  const activeDays = Number.isFinite(rawDays) && rawDays > 0 ? rawDays : DEFAULT_DAYS;

  const { data, failure, loading, refreshFailure } = useResource<PeopleResponse>(`people:${search}`, () => fetchPeople(search), refreshKey);

  // The Led AI-internal Procore layer. Asked for only when this is a staff
  // sign-in in a team workspace AND the roster just loaded shows the viewer
  // as an admin of it — so a member's page never touches the Procore routes
  // and never sees a refusal. With no Clerk in the browser (the ungated
  // local console) there is no email to hint on, so the roster's row alone
  // decides. The backend's gate (staff, then admin) decides for real; a
  // refusal still simply leaves the page as the customer sees it.
  const person = useAuthPerson();
  const viewerIsAdmin = data?.members.some((m) => m.isYou && m.role === "admin") ?? false;
  const staff = (person ? looksLikeLedStaff(person.email) : true) && params.get("workspace") !== "personal" && viewerIsAdmin;
  const procore = useProcoreBaseline(search, staff, refreshKey);
  const [lens, setLens] = useState<ProcoreLens>(DEFAULT_LENS);
  const procoreIndex = indexPull(procore.response?.pull ?? null, procore.response?.setup ?? null);
  // Invitations (for a chosen person not on Led AI yet) are read beside the
  // layer, not after it, so the page waits for both at once.
  const invitations = useInvitations(search, staff && TEAM_INVITES.enabled, refreshKey);

  if (failure) return <FailurePanel failure={failure} personalHref={personalHref} onGoPersonal={onGoPersonal} />;
  if (!data) return loading ? <LoadingPanel shape="people" /> : <EmptyPanel title="Team" body="Nothing loaded." />;
  // The page paints once: the roster waits for the Procore layer and the
  // invitations it may need, rather than the card arriving a beat later.
  if (staff && !(procore.settled && invitations.settled)) return <LoadingPanel shape="people" />;

  const pickRange = (days: number) => onNavigate(hrefWithParam("withinDays", String(days)));
  // The server's clock, not the browser's: `generatedAt` is when these counts
  // were true.
  const now = new Date(data.generatedAt).getTime();
  const members = data.members;
  const shown = showAll ? members : members.slice(0, TOP_ROWS);
  const walkers = members.filter((p) => p.walks > 0).length;
  const totals = members.reduce((sum, p) => ({ walks: sum.walks + p.walks, records: sum.records + p.itemsProduced }), { walks: 0, records: 0 });
  const windowLabel = `last ${data.withinDays} days`;
  const workspaceName = data.activeWorkspace.type === "team" ? data.activeWorkspace.name || "this workspace" : "this workspace";

  // Records, NOT records + photos: a walk that shot forty photos and raised
  // nothing produced nothing. The design's own stat excludes Photos too.
  const stats: Array<[string, string]> = [
    [String(totals.walks), `Walks ${windowLabel}`],
    [String(totals.records), "Records"],
    [`${walkers} of ${members.length}`, "People walked"],
  ];
  const inviteHref = hrefWithParam("page", "invite");
  const grid = procoreIndex ? PROCORE_GRID : BASE_GRID;
  const peopleCount = members.length + (procoreIndex?.pending.length ?? 0);

  return (
    <div className="sb" style={PANE}>
      <style>{`.teamrow:hover{background:${DS.rowHover}} .teamlink:hover{color:${DS.ink} !important}`}</style>
      <div style={PAGE} data-testid="team-page">
        <div style={{ display: "flex", flexWrap: "wrap", alignItems: "flex-end", justifyContent: "space-between", gap: "16px 24px" }}>
          <div style={{ minWidth: 0, maxWidth: 620 }}>
            <h1 style={{ margin: 0, fontSize: 30, fontWeight: 800, letterSpacing: "-1px", color: DS.ink }}>Team</h1>
            <p data-testid="team-scope" style={{ margin: "6px 0 0", fontSize: 14.5, lineHeight: 1.5, color: DS.body2, textWrap: "pretty" }}>
              {data.hasOrg
                ? `Walks and records on ${workspaceName}'s projects. Personal projects and other workspaces aren't counted.`
                : "Switch to a team workspace to see its members' activity."}
            </p>
          </div>
          <div style={{ flex: "none", display: "flex", alignItems: "center", gap: 10 }}>
            <RangePill activeDays={activeDays} onPick={pickRange} />
            {TEAM_INVITES.enabled && (
              <DsButton size="md" testId="team-invite" leftIcon={<DsIcon name="plus" size={14} color={DS.surface} />} onClick={() => onNavigate(inviteHref)} style={{ height: 40 }}>
                Invite
              </DsButton>
            )}
          </div>
        </div>

        {refreshFailure && <StaleBanner failure={refreshFailure} since={data.generatedAt} />}

        {/* No organization, or an organization we could not ask about. Two
            different facts, two different sentences — never one blank list. */}
        {data.rosterUnavailableReason ? (
          <div data-testid={data.hasOrg ? "roster-unavailable" : "no-organization"}>
            <EmptyPanel title={data.hasOrg ? "This organization's roster is unavailable" : "No organization"} body={data.rosterUnavailableReason} />
          </div>
        ) : (
          <>
            <StatBand stats={stats} />

            {procore.response && (
              <ProcoreCard search={search} response={procore.response} index={procoreIndex} lens={lens} onLens={setLens} onChanged={procore.refresh} card={CARD} />
            )}

            <div style={{ ...CARD, overflow: "hidden" }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "16px 22px 12px" }}>
                <span style={{ fontSize: 16, fontWeight: 800, letterSpacing: "-0.3px", color: DS.ink }}>
                  People <span style={{ color: DS.ink3, fontWeight: 700 }}>· {peopleCount}</span>
                </span>
                <span style={{ ...HEAD, fontSize: 9.5, fontWeight: 600, letterSpacing: "1.2px", color: DS.ink3 }}>{windowLabel}</span>
              </div>
              <div data-testid="team-table" style={{ overflowX: "auto" }}>
                <div style={{ minWidth: procoreIndex ? TABLE_MIN_WIDTH : TABLE_MIN_WIDTH - PROCORE_SPARK_W - 62 }}>
                  <div style={{ display: "grid", gridTemplateColumns: grid, alignItems: "end", gap: "0 6px", padding: "8px 18px 10px", borderTop: `1px solid ${DS.line}`, borderBottom: `1px solid ${DS.line}`, background: DS.rowHover }}>
                    <span style={HEAD}>Person</span>
                    <span style={{ ...HEAD, textAlign: "right" }}>Walks</span>
                    {RECORD_COLUMNS.map((column) => (
                      <span key={column} style={{ ...HEAD, textAlign: "right" }}>
                        {column}
                      </span>
                    ))}
                    <span style={{ ...HEAD, paddingLeft: 10 }}>Last walk</span>
                    {procoreIndex && (
                      <>
                        <span style={HEAD}>Procore / wk</span>
                        <span style={{ ...HEAD, textAlign: "right" }}>Change</span>
                      </>
                    )}
                  </div>

                  {members.length === 0 && (
                    <div data-testid="roster-empty" style={{ padding: 18, fontSize: 14, color: DS.body2, lineHeight: 1.5 }}>
                      This organization has no members yet. Add someone from Invite people.
                    </div>
                  )}

                  {shown.map((p) => {
                    const href = hrefWithParam("person", p.userId);
                    return <MemberRow key={p.userId} person={p} href={href} now={now} onOpen={() => onNavigate(href)} {...(procoreIndex ? { procore: { index: procoreIndex, lens } } : {})} />;
                  })}

                  {/* The pilot's people who are not on Led AI yet — drawn in the
                      roster so the roster IS the pilot. Their Led AI columns fill
                      in the day they sign in. */}
                  {procoreIndex?.pending.map((pending) => (
                    <PendingPersonRow
                      key={`pending-${pending.member.procoreUserId}`}
                      index={procoreIndex}
                      person={pending}
                      lens={lens}
                      grid={PROCORE_GRID}
                      recordColumns={RECORD_COLUMNS.length}
                      {...(TEAM_INVITES.enabled ? { invite: { search, canInvite: invitations.canInvite, reason: invitations.reason, byEmail: invitations.byEmail, onChanged: invitations.refresh } } : {})}
                    />
                  ))}

                  {members.length > TOP_ROWS && (
                    <button
                      data-testid="team-show-all"
                      className="kb"
                      onClick={() => setShowAll((value) => !value)}
                      style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 8, width: "100%", height: 48, border: "none", background: DS.rowHover, fontFamily: "inherit", fontSize: 14, fontWeight: 600, color: DS.body2, cursor: "pointer" }}
                    >
                      {showAll ? `Show top ${TOP_ROWS}` : `Show all ${members.length} people`}
                      <DsIcon name="chevron" size={12} color={DS.ink3} style={{ transform: showAll ? "rotate(-90deg)" : "rotate(90deg)" }} />
                    </button>
                  )}
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
